import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaMoneyBillWave,
  FaClock,
  FaTimesCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCreditCard,
  FaPhoneAlt,
  FaEnvelope
} from "react-icons/fa";

const Refund = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-12">
      <div className="w-full max-w-[1180px] mx-auto px-4 md:px-6">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <div className="flex items-center mb-4">
            <FaMoneyBillWave className="text-primary text-3xl mr-4" />
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800">
                Refund Policy
              </h1>
              <p className="text-gray-600 text-base md:text-lg mt-1 md:mt-2">
                Our cancellation and refund policy for CabZigo services.
                Your satisfaction is our priority.
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Last updated: 15 Aug 2025
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
          {/* Overview */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Policy Overview
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CabZigo provides transparent cancellation and refund policies
              to ensure fair treatment for both passengers and drivers. This policy
              outlines the terms for cancellations, refunds, and associated charges.
            </p>
          </section>

          {/* Cancellation Rules */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaTimesCircle className="text-red-500 mr-3" />
              Cancellation Rules
            </h2>

            <div className="grid grid-cols-1 gap-4 md:gap-6">
              {/* Free Cancellation */}
              <div className="bg-green-50 border border-green-200 p-4 md:p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FaCheckCircle className="text-green-500 text-xl mr-3" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-800">
                    Free Cancellation
                  </h3>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-700">
                    <strong>Within 5 minutes of booking:</strong> No charges applied
                  </p>
                  <p className="text-gray-700 text-sm">
                    You can cancel your ride without any penalty if you cancel
                    within 5 minutes of making the booking.
                  </p>
                </div>
              </div>

              {/* Standard Cancellation */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 md:p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FaClock className="text-yellow-500 text-xl mr-3" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-800">
                    Standard Cancellation Fee
                  </h3>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-700">
                    <strong>After 5 minutes:</strong> ₹20 cancellation fee
                  </p>
                  <p className="text-gray-700 text-sm">
                    If you cancel after 5 minutes but before the driver starts
                    moving towards your pickup location.
                  </p>
                </div>
              </div>

              {/* Driver En Route */}
              <div className="bg-orange-50 border border-orange-200 p-4 md:p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FaExclamationTriangle className="text-orange-500 text-xl mr-3" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-800">
                    Driver En Route Fee
                  </h3>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-700">
                    <strong>Driver started towards pickup:</strong> ₹50 cancellation fee
                  </p>
                  <p className="text-gray-700 text-sm">
                    Higher fee applies when the driver has already started driving
                    to your pickup location to compensate for their time and fuel.
                  </p>
                </div>
              </div>

              {/* No Show */}
              <div className="bg-red-50 border border-red-200 p-4 md:p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FaTimesCircle className="text-red-500 text-xl mr-3" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-800">
                    No Show Policy
                  </h3>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-700">
                    <strong>Customer no-show:</strong> Full fare charged
                  </p>
                  <p className="text-gray-700 text-sm">
                    If you don't show up at the pickup location within 5 minutes
                    of the driver's arrival, the full estimated fare will be charged.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Driver Cancellation */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Driver Cancellation
            </h2>
            <div className="bg-blue-50 border border-blue-200 p-4 md:p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <FaCheckCircle className="text-blue-500 text-xl mr-3" />
                <h3 className="text-base md:text-lg font-semibold text-gray-800">
                  100% Refund Guaranteed
                </h3>
              </div>
              <p className="text-gray-700 mb-3">
                If a driver cancels your ride for any reason, you will receive a
                full refund with no charges applied to your account.
              </p>
              <p className="text-gray-700 text-sm">
                The refund will be processed automatically and credited to your
                original payment method within the timeframes specified below.
              </p>
            </div>
          </section>

          {/* Refund Process */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCreditCard className="text-purple-500 mr-3" />
              Refund Processing Times
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds are processed back to your original payment method.
              Processing times vary by payment type:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left font-semibold">
                      Payment Method
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold">
                      Processing Time
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">Digital Wallet</td>
                    <td className="border border-gray-300 p-3 text-green-600 font-semibold">
                      Instant
                    </td>
                    <td className="border border-gray-300 p-3 text-sm">
                      Immediate credit to wallet balance
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">UPI</td>
                    <td className="border border-gray-300 p-3 text-green-600 font-semibold">
                      1-3 business days
                    </td>
                    <td className="border border-gray-300 p-3 text-sm">
                      Fast processing via payment gateway
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Credit Card</td>
                    <td className="border border-gray-300 p-3 text-yellow-600 font-semibold">
                      3-5 business days
                    </td>
                    <td className="border border-gray-300 p-3 text-sm">
                      Subject to bank processing times
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Debit Card</td>
                    <td className="border border-gray-300 p-3 text-yellow-600 font-semibold">
                      5-7 business days
                    </td>
                    <td className="border border-gray-300 p-3 text-sm">
                      Longer processing due to bank procedures
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Special Circumstances */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Special Circumstances
            </h2>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Technical Issues
                </h3>
                <p className="text-gray-700 text-sm">
                  If you experience app crashes, payment failures, or other technical
                  problems during booking, contact support for a full refund.
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Weather Conditions
                </h3>
                <p className="text-gray-700 text-sm">
                  During severe weather conditions that make travel unsafe,
                  cancellations may be eligible for reduced or waived fees.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Emergency Situations
                </h3>
                <p className="text-gray-700 text-sm">
                  Medical emergencies or other urgent situations will be handled
                  on a case-by-case basis with possible fee waivers.
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Safety Concerns
                </h3>
                <p className="text-gray-700 text-sm">
                  If you feel unsafe with a driver or vehicle, cancel immediately
                  and contact support. Safety-related cancellations incur no fees.
                </p>
              </div>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Dispute Resolution Process
            </h2>

            <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold">1</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Report Issue</h3>
                  <p className="text-gray-600 text-sm">
                    Contact support within 24 hours of the ride
                  </p>
                </div>

                <div className="text-center">
                  <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold">2</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Investigation</h3>
                  <p className="text-gray-600 text-sm">
                    Our team reviews the case within 48 hours
                  </p>
                </div>

                <div className="text-center">
                  <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold">3</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Resolution</h3>
                  <p className="text-gray-600 text-sm">
                    Refund or credit issued if applicable
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Important Notes */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Important Notes
            </h2>

            <div className="bg-yellow-50 border border-yellow-200 p-4 md:p-6 rounded-lg">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2 mt-1">•</span>
                  All refunds are processed in the same currency as the original payment
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2 mt-1">•</span>
                  Refund requests must be made within 30 days of the original booking
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2 mt-1">•</span>
                  Bank charges, if any, for refund processing may be deducted
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2 mt-1">•</span>
                  Multiple cancellations may result in temporary account restrictions
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2 mt-1">•</span>
                  Cash payments (COD) cannot be refunded; credit will be added to your wallet
                </li>
              </ul>
            </div>
          </section>

          {/* Contact Support */}
          <section className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Need Help with Refunds?
            </h2>
            <p className="text-gray-700 mb-4">
              Our customer support team is available 24/7 to help with refund
              requests and answer any questions about our policy.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <FaPhoneAlt className="text-primary mr-3" />
                <div>
                  <p className="font-semibold text-gray-800">Phone Support</p>
                  <p className="text-gray-600 text-sm">+91 7096386636</p>
                  <p className="text-gray-500 text-xs">Available 24/7</p>
                </div>
              </div>

              <div className="flex items-center">
                <FaEnvelope className="text-primary mr-3" />
                <div>
                  <p className="font-semibold text-gray-800">Email Support</p>
                  <p className="text-gray-600 text-sm">info@rajeshwarinfotech.com</p>
                  <p className="text-gray-500 text-xs">Response within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-gray-600 text-sm">
                <strong>Office Address:</strong> Rajkot, Gujarat, India
              </p>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-700 text-sm">
                <strong>Pro Tip:</strong> Include your booking ID and payment method
                in your refund request for faster processing.
              </p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-4 md:mt-8 text-center">
          <div className="inline-flex space-x-4">
            <Link
              to="/terms"
              className="text-primary hover:text-primary-dark underline"
            >
              Terms & Conditions
            </Link>
            <Link
              to="/privacy"
              className="text-primary hover:text-primary-dark underline"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;
